import { Controller } from "@hotwired/stimulus"

// Chrome for Android only — Web NFC (NDEFReader) is not available elsewhere.
// Ported from the APM's nfc_card_writer_controller, which is the version that has
// run in the field.
//
// Two-step association, split by a full page navigation rather than a hidden div.
// Both halves of the inherited rule have now been measured on a field Android:
//
//   The QR camera DOES poison Web NFC within a document. Open the camera and
//   NDEFReader stops reading for that document's lifetime; closing it does not
//   bring the reader back.
//   The poisoning does NOT survive a navigation. Step 1 runs the live scanner and
//   step 2 still arms.
//
// So the split is load-bearing, and it is the navigation — not any hiding — that
// carries it. Do not collapse these two steps into one document.
//
//   Step 1 — identify (camera allowed, NO NFC):
//     live QR scan or short code -> full navigation to ?short_code=… or ?uuid=…
//   Step 2 — associate (NFC only, NO camera):
//     fresh document -> the nfc-reader controller arms and dispatches nfc:read
//     -> POST associate -> back to the clean step 1 page.
//
// The Web NFC plumbing lives in nfc-reader, shared with the cashier code inputs;
// this controller only knows the association flow.
export default class extends Controller {
  static values = { identifyUrl: String, associateUrl: String }

  static targets = [
    "shortCodeInput", "identifyBtn",
    "shortCodeDisplay", "status", "restartBtn",
    "identifyUi"
  ]

  #busy = false
  #identifiedCard = null

  connect() {
    const params = new URLSearchParams(window.location.search)
    const card = params.get("short_code") ? { short_code: params.get("short_code") }
      : params.get("uuid") ? { uuid: params.get("uuid") }
        : null

    if (card) {
      this.#enterAssociateMode(card)
    } else {
      this.#status("Identifique o cartão pelo QR ou código curto.", "info")
    }
  }

  // The short_code charset excludes visually ambiguous characters (no 0/1/I/L/O) —
  // it mirrors NfcCard::SHORT_CODE_CHARSET, so a typo'd O never becomes a lookup.
  onShortCodeInput() {
    const value = this.shortCodeInputTarget.value.replace(/[^A-Z2-9]/gi, "").toUpperCase().slice(0, 6)
    this.shortCodeInputTarget.value = value
    this.identifyBtnTarget.disabled = value.length < 6
  }

  onShortCodeKeydown(event) {
    if (event.key === "Enter" && this.shortCodeInputTarget.value.length === 6) {
      event.preventDefault()
      this.identifyByShortCode()
    }
  }

  identifyByShortCode() {
    const shortCode = this.shortCodeInputTarget.value.toUpperCase()
    if (shortCode.length === 6) this.#goToAssociate({ short_code: shortCode })
  }

  // The printed QR carries the card uuid; a scanner pointed at something else may
  // hand back a short code instead, so both shapes are accepted.
  onQrRead({ detail: { code } }) {
    const raw = code.trim()
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)
    this.#goToAssociate(isUuid ? { uuid: raw } : { short_code: raw.toUpperCase() })
  }

  restart() {
    window.location.href = window.location.pathname
  }

  // The chip's own serial is what gets stored as chip_uid, so this deliberately
  // ignores the NDEF text payload that nfc-reader also carries.
  onNfcRead({ detail: { serial } }) {
    if (serial && this.#writable) this.#associateChip(serial)
  }

  // private

  // The reader is live from page load and a card left on it keeps firing, so this
  // turns away three different arrivals: taps landing before the lookup finished,
  // taps on a card that already carries a chip, and repeats while a POST is still
  // in flight — that last one would come back as "already associated".
  get #writable() {
    return !this.#busy && this.#identifiedCard && !this.#identifiedCard.chip_uid
  }

  // Step 1 and step 2 are separate documents; see the header for what that split is
  // and is not known to buy.
  #goToAssociate(param) {
    window.location.href = `${window.location.pathname}?${new URLSearchParams(param)}`
  }

  async #enterAssociateMode(cardParam) {
    this.identifyUiTarget.hidden = true
    this.restartBtnTarget.hidden = false

    this.#status("Buscando cartão…", "info")

    try {
      this.#cardFound(await this.#post(this.identifyUrlValue, cardParam))
    } catch (error) {
      this.#status(`${error.message} Toque em "Recomeçar" e tente de novo.`, "error")
    }
  }

  #cardFound(card) {
    this.#identifiedCard = card
    this.shortCodeDisplayTarget.textContent = card.short_code
    this.shortCodeDisplayTarget.hidden = false

    if (card.chip_uid) {
      this.#status("Este cartão já tem um chip associado. Não é possível regravá-lo.", "error")
      return
    }

    this.#status("✓ Cartão encontrado. Aproxime o chip NFC do cartão.", "ok")
  }

  async #associateChip(chipUid) {
    this.#busy = true
    this.#status("Associando chip…", "info")

    try {
      const card = await this.#post(this.associateUrlValue, {
        chip_uid: chipUid, short_code: this.#identifiedCard.short_code
      })
      this.#status(`✓ Concluído: ${card.short_code}. Voltando…`, "ok")
      setTimeout(() => this.restart(), 1500)
    } catch (error) {
      this.#status(error.message, "error")
      this.#busy = false
    }
  }

  async #post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || `Erro do servidor (${response.status}).`)
    }
    return response.json()
  }

  #status(message, tone) {
    this.statusTarget.textContent = message
    this.statusTarget.dataset.tone = tone
  }
}
